// client/src/coms/views/games_parts/GamesHub.js
// Voxemera Activities (the former Games destination; the tab id stays "games"): one catalogue for every
// activity (GameNest board, card and real-time games and Texas Hold'em on the hosted-session engine, the
// arcade games, the drawing games on the party engine, the classic canvas game and Watch Together), one
// invitation system (shared codes, links and friend invites) and one place to return to what you are in.
// Opened from a server's sidebar it is scoped to that server: hosting offers "everyone in this server"
// and the open games listed are that server's (plus friends' games and invitations).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../canvas_parts/party/partyGames.css';
import GamesCatalogue from './GamesCatalogue';
import GamesHostDialog from './GamesHostDialog';
import GameSessionView from './GameSessionView';
import useGameSession, { describeGameError, gamesApi } from './useGameSession';
import { getActiveGame, setActiveGame, subscribeActiveGame } from './activeGameStore';
import { GamesVisibleContext } from './gamesVisibility';
import PartySessionScreen, { Toasts } from '../canvas_parts/party/PartySessionScreen';
import { PartyResultPage } from '../canvas_parts/party/PartyResults';
import { describePartyError } from '../canvas_parts/party/usePartySession';
import { mergeSessions } from './gamesData';
import WatchActivityView from './WatchActivityView';
import WatchHostDialog from './WatchHostDialog';
import { WATCH_ERRORS, describeWatchError } from './useWatchActivity';

const POLL_MS = 6000;
const GENERIC_ERROR = describePartyError('');

export function describeAnyGameError(code) {
  if (WATCH_ERRORS[code]) return WATCH_ERRORS[code];
  const party = describePartyError(code);
  return party !== GENERIC_ERROR ? party : describeGameError(code);
}

function readFriends(result) {
  return ((result && result.data && result.data.friends) || []).map((item) => ({
    id: Number(item.other_user_id),
    username: (item.other_user && item.other_user.username) || `User ${item.other_user_id}`,
  })).filter((friend) => friend.id > 0);
}

export default function GamesHub({
  user,
  server = null,
  canvasId = 0,
  drawingOnly = false,
  visible = true,
  pendingCode = '',
  onPendingCodeHandled,
  initial = {},
  onOpenTab,
  onClose,
  voiceSession = null,
  onJoinVoice = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const meId = Number(user && user.id) || 0;
  // Shell servers carry a public id (a string) and their database id; game eligibility uses the latter.
  const serverId = server ? Number(server.db_id != null ? server.db_id : server.server_id != null ? server.server_id : server.id) || 0 : 0;
  const scopedServer = serverId ? { id: serverId, name: String(server.name || 'this server') } : null;
  const [catalog, setCatalog] = useState([]);
  const [partyModes, setPartyModes] = useState([]);
  const [hosted, setHosted] = useState([]);
  const [partySessions, setPartySessions] = useState([]);
  const [watchSessions, setWatchSessions] = useState([]);
  const [watchHosting, setWatchHosting] = useState(false);
  const [friends, setFriends] = useState([]);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState('');
  const [hosting, setHosting] = useState(null);
  const [hostBusy, setHostBusy] = useState(false);
  const [hostError, setHostError] = useState('');
  const [joining, setJoining] = useState(false);
  const [resultId, setResultId] = useState(initial.resultId || '');
  const [launch, setLaunch] = useState(() => {
    const pending = typeof window !== 'undefined' ? window.__voxPendingGamesContext : null;
    if (typeof window !== 'undefined') window.__voxPendingGamesContext = null;
    return { category: (pending && pending.category) || (drawingOnly ? 'Drawing' : 'All'), canvasId: Number((pending && pending.canvasId) || canvasId) || 0, key: 0 };
  });
  const [active, setActive] = useState(() => (initial.sessionId ? { kind: 'party', id: initial.sessionId, name: 'Game', intent: 'join' } : (getActiveGame() ? { ...getActiveGame(), intent: 'restore' } : null)));
  const [browsing, setBrowsing] = useState(false);

  // Games opened elsewhere in this tab (another Games view, an invitation link) take over this one.
  useEffect(() => subscribeActiveGame((next) => {
    setActive((current) => {
      if (!next) return null;
      if (current && current.kind === next.kind && current.id === next.id) return { ...current, name: next.name };
      return { ...next, intent: 'restore' };
    });
  }), []);

  const openGame = useCallback((game, intent = '') => {
    setActiveGame({ kind: game.kind, id: game.id, name: game.name || 'Game', serverId: serverId || '' });
    setActive({ kind: game.kind, id: game.id, name: game.name || 'Game', intent });
    setBrowsing(false);
    setResultId('');
  }, [serverId]);

  const refresh = useCallback(async () => {
    const scope = serverId ? `?serverId=${serverId}` : '';
    const partyScope = [serverId ? `serverId=${serverId}` : '', launch.canvasId && drawingOnly ? `canvasId=${launch.canvasId}` : ''].filter(Boolean).join('&');
    const [hostedResult, partyResult, watchResult] = await Promise.all([
      drawingOnly ? Promise.resolve(null) : gamesApi(`/api/games/sessions${scope}`).catch(() => null),
      gamesApi(`/api/party/sessions${partyScope ? `?${partyScope}` : ''}`).catch(() => null),
      drawingOnly ? Promise.resolve(null) : gamesApi(`/api/activities/watch${scope}`).catch(() => null),
    ]);
    if (hostedResult && hostedResult.ok) setHosted(hostedResult.data.sessions || []);
    if (partyResult && partyResult.ok) setPartySessions(partyResult.data.sessions || []);
    if (watchResult && watchResult.ok) setWatchSessions(watchResult.data.activities || []);
  }, [drawingOnly, launch.canvasId, serverId]);

  const exitGame = useCallback((message) => {
    setActiveGame(null);
    setActive(null);
    setBrowsing(false);
    if (message) setError(message);
    refresh();
    gamesApi('/api/party/stats').then((r) => r.ok && setStats(r.data.stats)).catch(() => {});
    gamesApi('/api/party/results').then((r) => r.ok && setRecent(r.data.results || [])).catch(() => {});
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      gamesApi('/api/games/catalog'),
      gamesApi('/api/party/modes'),
      gamesApi('/api/coms/friends'),
      gamesApi('/api/party/stats'),
      gamesApi('/api/party/results'),
    ]).then(([catalogResult, modesResult, friendsResult, statsResult, recentResult]) => {
      if (cancelled) return;
      if (!catalogResult.ok) setError(catalogResult.status === 401 ? 'Sign in to use Activities.' : 'Activities are unavailable right now.');
      setCatalog((catalogResult.data && catalogResult.data.games) || []);
      setPartyModes((modesResult.ok && modesResult.data.modes) || []);
      setFriends(readFriends(friendsResult));
      setStats(statsResult.ok ? statsResult.data.stats : null);
      setRecent(recentResult.ok ? recentResult.data.results || [] : []);
    }).catch(() => {
      if (!cancelled) setError('Activities are unavailable right now.');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showingSession = Boolean(active) && !browsing && !resultId;

  useEffect(() => {
    if (!visible || showingSession) return undefined;
    refresh();
    const timer = window.setInterval(() => {
      if (!document.hidden) refresh();
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh, showingSession, visible]);

  // Opening Games from Canvas (or a canvas room) picks the drawing games and that canvas.
  useEffect(() => {
    const onOpen = (event) => {
      const detail = (event && event.detail) || window.__voxPendingGamesContext || {};
      window.__voxPendingGamesContext = null;
      // Opened to show the activity that was just made active (a voice channel's Watch Together banner).
      if (detail.openActive) {
        setBrowsing(false);
        setResultId('');
        return;
      }
      setLaunch((current) => ({ category: detail.category || current.category, canvasId: Number(detail.canvasId) || 0, key: current.key + 1 }));
      setBrowsing(true);
      if (detail.watch) setWatchHosting(true);
    };
    window.addEventListener('vox-open-games', onOpen);
    return () => window.removeEventListener('vox-open-games', onOpen);
  }, []);

  useEffect(() => {
    if (!error) return undefined;
    const timer = window.setTimeout(() => setError(''), 4500);
    return () => window.clearTimeout(timer);
  }, [error]);

  // ---- joining by invitation code: one endpoint for every game
  const joinCode = useCallback(async (code) => {
    setJoining(true);
    const result = await gamesApi('/api/games/join', { method: 'POST', body: { code } }).catch(() => null);
    setJoining(false);
    if (!result || !result.ok) {
      setError(describeAnyGameError(result && result.data && result.data.error));
      return false;
    }
    if (result.data.kind === 'canvas') {
      // Classic Draw & Guess: the code added this player to the host's canvas; open it with the game panel.
      const query = `?canvasId=${encodeURIComponent(result.data.canvasId)}&game=sketch-relay`;
      if (typeof onOpenTab === 'function') {
        navigate(`${location.pathname}${query}`);
        onOpenTab('canvas');
      } else navigate(`/canvas${query}`);
      return true;
    }
    if (result.data.kind === 'watch') openGame({ kind: 'watch', id: result.data.id, name: (result.data.activity && result.data.activity.title) || 'Watch Together' }, '');
    else if (result.data.kind === 'party') openGame({ kind: 'party', id: result.data.id, name: result.data.view && result.data.view.mode ? result.data.view.mode.name : 'Game' }, '');
    else openGame({ kind: 'session', id: result.data.id, name: result.data.session ? result.data.session.gameName : 'Game' }, '');
    return true;
  }, [location.pathname, navigate, onOpenTab, openGame]);

  useEffect(() => {
    if (!pendingCode) return;
    if (typeof onPendingCodeHandled === 'function') onPendingCodeHandled();
    joinCode(pendingCode);
  }, [joinCode, onPendingCodeHandled, pendingCode]);

  useEffect(() => {
    if (!initial.code) return;
    joinCode(initial.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- opening a listed game
  const openListed = useCallback(async (item, intent) => {
    if (item.kind === 'watch') {
      openGame({ kind: 'watch', id: item.id, name: item.title || 'Watch Together' }, '');
      return;
    }
    if (item.kind === 'party') {
      openGame(item, intent === 'open' ? '' : intent);
      return;
    }
    if (!item.member) {
      const joined = await gamesApi(`/api/games/sessions/${encodeURIComponent(item.id)}/actions`, { method: 'POST', body: { action: 'join' } }).catch(() => null);
      if (!joined || !joined.ok) {
        setError(describeGameError(joined && joined.data && joined.data.error));
        refresh();
        return;
      }
    }
    openGame(item, '');
  }, [openGame, refresh]);

  // ---- Watch Together: its own session; a voice channel of this server is optional
  const joinVoice = useCallback((serverPublicId, channelId) => {
    if (typeof onJoinVoice === 'function') onJoinVoice(String(serverPublicId), String(channelId));
  }, [onJoinVoice]);
  const createWatch = useCallback(async (choice) => {
    setHostBusy(true);
    setHostError('');
    const body = { title: choice.title, controlMode: choice.controlMode, audience: choice.audience };
    if (choice.channelId && serverId) Object.assign(body, { serverId, channelId: choice.channelId });
    const result = await gamesApi('/api/activities/watch', { method: 'POST', body }).catch(() => null);
    setHostBusy(false);
    if (!result || !result.ok) {
      setHostError(describeWatchError(result && result.data && result.data.error, 'Could not start Watch Together.'));
      return;
    }
    const activity = result.data.activity;
    setWatchHosting(false);
    openGame({ kind: 'watch', id: activity.id, name: activity.title || 'Watch Together' }, '');
    refresh();
  }, [openGame, refresh, serverId]);

  // ---- hosting
  const createGame = useCallback(async (entry, choice) => {
    setHostBusy(true);
    setHostError('');
    try {
      if (entry.kind === 'canvas') {
        const created = await gamesApi('/api/canvas', { method: 'POST', body: { name: entry.name } });
        const newCanvasId = created.data && created.data.canvas && created.data.canvas.id;
        if (!created.ok || !newCanvasId) throw new Error('Could not create a canvas for the game.');
        for (const friendId of choice.invitedUserIds) {
          // eslint-disable-next-line no-await-in-loop
          await gamesApi(`/api/canvas/${newCanvasId}/invite`, { method: 'POST', body: { user_id: friendId } });
        }
        setHosting(null);
        const query = `?canvasId=${encodeURIComponent(newCanvasId)}&game=sketch-relay`;
        // In the Voxemera shell the game opens in the Canvas area; elsewhere on the Canvas page.
        if (typeof onOpenTab === 'function') {
          navigate(`${location.pathname}${query}`);
          onOpenTab('canvas');
        } else {
          navigate(`/canvas${query}`);
        }
        return;
      }
      const result = entry.kind === 'party'
        ? await gamesApi('/api/party/sessions', {
          method: 'POST',
          body: { modeId: entry.modeId, visibility: choice.visibility, serverId: choice.serverId, canvasId: launch.canvasId || 0, invitedUserIds: choice.invitedUserIds, preset: choice.preset },
        })
        : await gamesApi('/api/games/sessions', {
          method: 'POST',
          body: { gameKey: entry.key, visibility: choice.visibility, serverId: choice.serverId, invitedUserIds: choice.invitedUserIds },
        });
      if (!result.ok) throw new Error(describeAnyGameError(result.data && result.data.error));
      setHosting(null);
      if (entry.kind === 'party') openGame({ kind: 'party', id: result.data.view.id, name: entry.name }, '');
      else openGame({ kind: 'session', id: result.data.session.id, name: entry.name }, '');
      refresh();
    } catch (err) {
      setHostError(err.message || 'Could not create the game.');
    } finally {
      setHostBusy(false);
    }
  }, [launch.canvasId, location.pathname, navigate, onOpenTab, openGame, refresh]);

  // ---- the hosted (GameNest) game on screen
  const nest = useGameSession(active && active.kind === 'session' ? active.id : '');
  useEffect(() => {
    if (active && active.kind === 'session' && nest.closed) exitGame(active.intent === 'restore' ? '' : 'This game is no longer available.');
  }, [active, exitGame, nest.closed]);
  useEffect(() => {
    if (active && active.kind === 'session' && nest.session && nest.session.gameName && active.name !== nest.session.gameName) {
      setActiveGame({ kind: 'session', id: active.id, name: nest.session.gameName, serverId: serverId || '' });
    }
  }, [active, nest.session, serverId]);
  const onPartyView = useCallback((view) => {
    if (view && view.mode && active && active.kind === 'party' && active.name !== view.mode.name) {
      setActiveGame({ kind: 'party', id: active.id, name: view.mode.name, serverId: serverId || '' });
    }
  }, [active, serverId]);

  const entries = useMemo(() => (drawingOnly ? catalog.filter((entry) => entry.kind !== 'session') : catalog), [catalog, drawingOnly]);
  const sessions = useMemo(() => mergeSessions(hosted, partySessions, catalog, drawingOnly ? [] : watchSessions, meId), [catalog, drawingOnly, hosted, meId, partySessions, watchSessions]);

  return (
    <GamesVisibleContext.Provider value={visible && showingSession}>
      <div className="party-root games-hub" data-testid="games-hub">
        {onClose ? (
          <div className="party-overlay-bar">
            <span>🎮 Activities</span>
            <button type="button" className="party-btn small ghost" onClick={onClose} data-testid="party-close">Back to canvas</button>
          </div>
        ) : null}
        {active && !showingSession ? (
          <div className="games-return-banner" role="status" data-testid="games-return">
            <span>You are in <strong>{active.name || 'an activity'}</strong>. It keeps going while you look around.</span>
            <button type="button" className="party-btn small" data-testid="return-to-activity" onClick={() => { setBrowsing(false); setResultId(''); }}>Return to activity</button>
          </div>
        ) : null}
        {active ? (
          <div className="games-session-host" style={{ display: showingSession ? 'flex' : 'none' }}>
            {active.kind === 'watch' ? (
              <WatchActivityView
                key={active.id}
                activityId={active.id}
                user={user}
                voiceSession={voiceSession}
                onJoinVoice={joinVoice}
                onBack={() => setBrowsing(true)}
                onExit={exitGame}
              />
            ) : active.kind === 'party' ? (
              <PartySessionScreen
                key={active.id}
                sessionId={active.id}
                intent={active.intent}
                friends={friends}
                server={scopedServer}
                modes={partyModes}
                onExit={exitGame}
                onBack={() => setBrowsing(true)}
                onView={onPartyView}
              />
            ) : (
              <GameSessionView
                game={nest}
                currentUserId={meId}
                friends={friends}
                onBack={() => setBrowsing(true)}
                onLeft={() => exitGame('')}
              />
            )}
          </div>
        ) : null}
        {!showingSession ? (
          resultId
            ? <PartyResultPage resultId={resultId} meId={meId} onBack={() => setResultId('')} />
            : (
              <GamesCatalogue
                key={launch.key}
                entries={entries}
                sessions={sessions}
                stats={stats}
                recent={recent}
                serverName={scopedServer ? scopedServer.name : ''}
                initialCategory={launch.category}
                joining={joining}
                onHost={(entry) => {
                  setHostError('');
                  if (entry.kind === 'watch') setWatchHosting(true);
                  else setHosting(entry);
                }}
                onOpenSession={openListed}
                onJoinCode={joinCode}
                onOpenResult={setResultId}
              />
            )
        ) : null}
        {hosting ? (
          <GamesHostDialog
            entry={hosting}
            friends={friends}
            server={scopedServer}
            canvasId={launch.canvasId}
            busy={hostBusy}
            error={hostError}
            onCancel={() => setHosting(null)}
            onCreate={(choice) => createGame(hosting, choice)}
          />
        ) : null}
        {watchHosting ? (
          <WatchHostDialog
            server={server}
            voiceSession={voiceSession}
            busy={hostBusy}
            error={hostError}
            onCancel={() => setWatchHosting(false)}
            onCreate={createWatch}
          />
        ) : null}
        <Toasts error={error} notice="" />
      </div>
    </GamesVisibleContext.Provider>
  );
}
